import { useEffect, useRef, useState } from 'react';
import type { Manager, Socket } from 'socket.io-client';
import { createSocketManager } from '@/lib/socket';
import type { SocketConfig } from '@/types/sale';
import type { EdcPhase, EdcState } from '@/types/payment';

interface LatestStatusPayload {
  status: 'resumed' | 'approved' | 'declined' | 'unresolved' | 'failed' | string;
  reason?: string;
  /**
   * Server's answer to "may this cart be charged again?". Derived there from
   * `edc.state`, so DECLINED and FAILED_NOT_SENT are safe while UNRESOLVED —
   * where the card may already have been charged — is not.
   */
  retrySafe?: boolean;
  data?: {
    panMasking?: string;
    cardHolderName?: string;
    approvalCode?: string;
    receiptTraceNo?: string;
    responseCode?: string;
    state?: string | null;
  };
}

interface UseEdcSocketArgs {
  config: SocketConfig | null;
  open: boolean;
}

interface UseEdcSocketReturn {
  state: EdcState;
  /** Whether a fresh charge against this cart is safe. Undefined until known. */
  retrySafe: boolean | undefined;
  reset: () => void;
}

const IDLE: EdcState = { phase: 'idle' };

/**
 * Where a transaction had already got to when we (re)connected.
 *
 * On connect the server replies with `{ status: 'resumed', data: { state } }`
 * so a client joining late is not left waiting for an event that already fired.
 * The outcome lives in `state`, not in `status`.
 *
 * Reading only `status` collapsed every terminal state onto `waiting_card`, so
 * a POS that reconnected after the payment concluded told the cashier to
 * present a card for a transaction that was already settled, declined, or
 * parked for a human. Absent here — ARMED, or no state at all — genuinely is
 * "still waiting", so it falls through to the same default.
 */
const RESUMED_PHASE: Readonly<Record<string, EdcPhase>> = {
  SETTLED: 'approved',
  DECLINED: 'declined',
  // Never reached the terminal, so nothing was charged — same as `failed`.
  FAILED_NOT_SENT: 'declined',
  // The card may have been charged. Only the printed slip can say.
  UNRESOLVED: 'unresolved',
  APPROVED_UNTICKETED: 'unresolved',
  SETTLED_OVERSOLD: 'unresolved',
  AWAITING_RESPONSE: 'processing',
};

/** Exported for test: the catch-up's state is the whole decision. */
export function resumedPhase(state?: string | null): EdcPhase {
  return RESUMED_PHASE[state ?? ''] ?? 'waiting_card';
}

/**
 * Subscribe to an EDC transaction's `/edc` channel and reduce it to a state
 * machine.
 *
 * Deliberately narrower than useCashlessSocket: there is NO cancel. The Permata
 * protocol has no cancel message, and a card already in the terminal cannot be
 * recalled from a browser — offering the button would imply otherwise. Closing
 * the dialog stops the cashier watching; it does not stop the payment.
 */
export function useEdcSocket({ config, open }: UseEdcSocketArgs): UseEdcSocketReturn {
  const [state, setState] = useState<EdcState>(IDLE);
  const [retrySafe, setRetrySafe] = useState<boolean | undefined>(undefined);
  const managerRef = useRef<Manager | null>(null);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!open || !config?.url || !config?.token || !config?.namespace) {
      return;
    }

    setState({ phase: 'connecting' });

    const manager = createSocketManager(config.url, { forceNew: false });
    managerRef.current = manager;
    const socket = manager.socket(config.namespace, { auth: { token: config.token } });
    socketRef.current = socket;

    socket.on('connect', () => {
      setState((prev) => (prev.phase === 'connecting' ? { phase: 'waiting_card' } : prev));
    });

    socket.on('latest_status', (raw: string) => {
      try {
        const data: LatestStatusPayload = typeof raw === 'string' ? JSON.parse(raw) : raw;
        // Recorded independently of the phase: whether money moved is the
        // server's call, and the phase is only how we render it.
        if (data.retrySafe !== undefined) setRetrySafe(data.retrySafe);

        switch (data.status) {
          case 'resumed': {
            // A reconnect mid-transaction. The server tells us where it got to
            // so the cashier is not shown "waiting" for something already done.
            const phase = resumedPhase(data.data?.state);
            setState({ phase });

            // A concluded transaction sends nothing further, so stop watching
            // it — the same thing the live `approved` and `declined` cases do.
            // `unresolved` deliberately stays connected: it is not concluded,
            // and a late answer from the terminal must still be able to land.
            if (phase === 'approved' || phase === 'declined') {
              socket.emit('force_disconnect', 'close');
            }

            // NOTE: `retrySafe` is left as-is. The catch-up carries only a
            // state, and deriving "may this cart be charged again?" here would
            // put a second copy of that rule next to the server's — which is
            // exactly the kind of pair that drifts. Unknown is also the safe
            // direction: refusing a retry costs one manual re-sale, wrongly
            // allowing one can charge a customer twice.
            break;
          }

          case 'approved':
            setState({
              phase: 'approved',
              panMasking: data.data?.panMasking,
              cardHolderName: data.data?.cardHolderName,
              approvalCode: data.data?.approvalCode,
              receiptTraceNo: data.data?.receiptTraceNo,
            });
            socket.emit('force_disconnect', 'close');
            break;

          case 'declined':
            setState({ phase: 'declined', reason: data.reason });
            socket.emit('force_disconnect', 'close');
            break;

          case 'unresolved':
            // NOT a failure. The terminal never answered, so the card may have
            // been charged — the cashier has to read the slip.
            setState({ phase: 'unresolved', reason: data.reason });
            break;

          case 'failed':
            // Never reached the terminal at all, so nothing was charged.
            setState({ phase: 'declined', reason: data.reason });
            socket.emit('force_disconnect', 'close');
            break;

          default:
            break;
        }
      } catch {
        /* malformed payload — ignore */
      }
    });

    socket.on('connect_error', () => {
      // A socket problem tells us nothing about the payment: the backend keeps
      // driving it either way. Say so rather than implying failure.
      setState({ phase: 'error', message: 'Status mesin EDC tidak terpantau. Cek mesin EDC.' });
    });

    return () => {
      try {
        socket.removeAllListeners();
        socket.disconnect();
      } catch {
        /* ignore */
      }
      socketRef.current = null;
      managerRef.current = null;
    };
  }, [open, config?.url, config?.token, config?.namespace]);

  function reset() {
    setState(IDLE);
    setRetrySafe(undefined);
  }

  return { state, retrySafe, reset };
}
